import { useFormContext } from 'react-hook-form';
import type { TLocalize } from '@hooks/use-localize';
// import MentorOfferSection from '@views/register/lab-orders-sections/mentor-offer-section';
import type { ILabOrdersFormValues, ILabOrdersModel } from '@/types/history/lab-orders';
import FieldError from '@components/field-error';

interface IProps {
    model: ILabOrdersModel;
    t: TLocalize;
    isSubmitting: boolean;
}

const QUEST_LOCATIONS_URL =
    'https://appointment.questdiagnostics.com/find-location/as-location-finder';

const styles = `
.pwl-options-row { display: flex; gap: 16px; margin-bottom: 20px; }
.pwl-option-card { flex: 1; border: 1px dashed rgba(204,204,204,1); border-radius: 12px; padding: 30px; cursor: pointer; }
.pwl-option-card input[type=radio] { margin-top: 3px; flex-shrink: 0; }
.pwl-option-card label { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 0; cursor: pointer; }
.pwl-option-content { display: flex; flex-direction: column; gap: 6px; }
.pwl-option-content strong { color: rgba(51,51,51,1); font-family: 'Montserrat', sans-serif; font-size: 16px; font-weight: 700; line-height: 20px; }
.pwl-option-content p { font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 400; color: rgba(0,0,0,1); margin-bottom: 0; line-height: 20px; }
.pwl-option-content a { font-family: 'Montserrat', sans-serif; font-size: 14px; line-height: 20px; }
.pwl-distance { margin-top: 8px; margin-bottom: 0; font-size: 14px; }
.mentor-card { background: rgba(239,244,249,1); border: 1px solid rgba(220,228,240,1); border-radius: 5px; padding: 30px; margin-bottom: 20px; display: flex; gap: 30px; align-items: flex-start; }
.mentor-card-body { flex: 1; display: flex; flex-direction: column; gap: 10px; }
.mentor-title { margin: 0; font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 16px; color: rgba(200,16,46,1); }
.mentor-text { margin: 0; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 400; color: rgba(0,0,0,1); line-height: 1.5; }
.mentor-question .control-label { display: block; font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 14px; margin-bottom: 8px; }
.mentor-radios .radio-inline + .radio-inline { margin-left: 20px; }
.mentor-video { flex: 0 0 40%; }
`;

/** The donor hasn't started labs yet and picks how to complete them. */
const ChooseOptionSection = ({ model, t, isSubmitting }: IProps) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<ILabOrdersFormValues>();

    return (
        <>
            <style>{styles}</style>

            <h1>{t('DashLabel_PreWorkupLabs', 'Next Step: Pre-Workup Labs')}</h1>
            <p>
                {t('DashLabel_PreWorkupLabsIntro', 'Pre-workup labs are the next step in determining your eligibility to donate and are provided at no cost to you.')}
            </p>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    border: '1px solid #31708f',
                    borderRadius: 4,
                    padding: '12px 15px',
                    backgroundColor: '#d9edf7',
                    color: '#31708f',
                }}
            >
                <span
                    className="glyphicon glyphicon-info-sign"
                    style={{ flexShrink: 0, marginTop: 2 }}
                ></span>
                <div>
                    <a
                        href={QUEST_LOCATIONS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#C8102E', textDecoration: 'underline' }}
                    >
                        {t('DashLabel_ClickHereToSeeQuestLocations', 'Click here to see Quest Diagnostics locations')}
                    </a>
                    <br />
                    {t('DashLabel_QuestLocationReturnMessage', 'Please return to this screen to choose your pre-workup lab option.')}
                </div>
            </div>

            <p style={{ marginTop: 15 }}>
                <strong>
                    {t('DashLabel_PreWorkupLabsSelectPrompt', 'Select one of the following two options to complete your pre-workup labs:')}
                </strong>
            </p>

            <div className="pwl-options-row" style={{ marginBottom: 0 }}>
                <div className="pwl-option-card">
                    <label>
                        <input type="radio" value="quest" {...register('labChoice')} />
                        <div className="pwl-option-content">
                            <strong>
                                {t('DashLabel_OptionQuestTitle', 'Visit a Quest lab in person')}
                            </strong>
                            <p>
                                {t('DashLabel_QuestDescription', 'Download a lab requisition form and take it to a Quest Diagnostics location near you.')}
                            </p>
                            {model.distanceToQuest > 0 && (
                                <p className="pwl-distance">
                                    <em>
                                        {t('DashLabel_NearestQuestDistance', 'Your nearest Quest Diagnostics is approximately')}{' '}
                                        <strong>
                                            {model.distanceToQuest} {t('Word_Miles', 'miles')}
                                        </strong>{' '}
                                        {t('Word_Away', 'away.')}
                                    </em>
                                </p>
                            )}
                        </div>
                    </label>
                </div>

                {model.canChoosePhlebot && (
                    <div className="pwl-option-card">
                        <label>
                            <input type="radio" value="phlebot" {...register('labChoice')} />
                            <div className="pwl-option-content">
                                <strong>
                                    {t('DashLabel_OptionPhlebotTitle', 'Schedule an appointment with a mobile phlebotomist')}
                                </strong>
                                <p>
                                    {t('DashLabel_PhlebotDescription', "No lab visit needed — we'll send you a kit and a phlebotomist will call you to schedule a blood draw at your home, workplace, or another location of your choice.")}
                                </p>
                            </div>
                        </label>
                    </div>
                )}
            </div>

            <FieldError message={errors.labChoice?.message} block={true} />

            {/* {model.showMentorOffer && <MentorOfferSection t={t} />} */}

            <button type="submit" className="btn btn-primary" disabled={isSubmitting} style={{ marginTop: 20 }}>
                {t('Word_Continue', 'Continue')}
            </button>
        </>
    );
};

export default ChooseOptionSection;
